<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"

const NOTICE_CANCELED_REASON_MAX_LENGTH = 1000
const operationStore = useOperationStore()

const schema = yup.object().shape({
  shipped: yup.boolean().test(
      "is-checked",
      "Der Versand muss bestätigt werden",
      yupTestOnlyOnCreate((value) => value === true)
  ),

  shippingConfirmationDate: yup.string().when("shipped", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

watch(
  () => state.shipped,
  (value) => {
    if (value === false) {
      state.shippingConfirmationDate = undefined
    }
    operationStore.unsavedChanges = false
  },
  { immediate: true }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Zwischenbescheid"
      />
    </template>
    <IffContentSection
      headline="Zwischenbescheid"
      :is-last-child="true"
    >
      <div id="shipped-region" aria-live="polite">
        <IffTransitionHeight>
          <div>
            <UFormGroup name="shipped" class="w-[600px]">
              <UCheckbox
                label="Der Versand der erforderlichen Dokumente an die antragstellende Person ist erfolgt"
                v-model="state.shipped"
                required
                aria-required="true"
                aria-controls="shipping-confirmation-date-region"
              />
            </UFormGroup>
            <div id="shipping-confirmation-date-region" aria-live="polite">
              <IffTransitionHeight>
                <UFormGroup
                  name="shippingConfirmationDate"
                  label="Datum Zwischenbescheid"
                  v-if="state.shipped"
                  required
                  class="max-w-[250px]"
                >
                  <template #hint>
                    <IffIconTooltip
                      text="BQFG: Wert wird in Datum der Entscheidung (e08) übernommen."
                    />
                    <UButton
                      @click="
                        state.shippingConfirmationDate = new Date()
                          .toISOString()
                          .slice(0, 10)
                      "
                      icon="i-ic-outline-access-time"
                      size="sm"
                      color="gray"
                      variant="ghost"
                      :trailing="false"
                      aria-label="Aktuelles Datum wählen"
                      title="Aktuelles Datum wählen"
                    />
                  </template>

                  <UInput
                    v-model="state.shippingConfirmationDate"
                    type="date"
                    aria-required="true"
                    class="whitespace-normal max-w-[150px]"
                  />
                </UFormGroup>
              </IffTransitionHeight>
            </div>
          </div>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
